/**
 * 发品 / 模板币种来源分流：
 * - Temu 半托/全托：publish/standard 的 currencyType（CNY/USD）
 * - Temu 自运营：站点列表币种（若已接入）；否则同样回退 publish/standard
 *
 * 注意：Temu 没有 Shein query-publish-fill-in-standard。
 */
import { extractPublishCurrencies, extractDefaultPublishCurrency, pickPublishCurrency } from '@/temu-platform/utils/temuPublishFillInStandard';
import { temuShopCapabilities } from '@/temu-platform/utils/temuShopCapabilities';
import {
  extractSiteListCurrencies,
  pickSiteListCurrency,
} from '@/temu-platform/utils/temuSiteList';

export const FILL_IN_CURRENCY_EMPTY_WARNING = '未从 Temu 发品规范解析到可用币种（CNY/USD），供货价可能无法提交';
export const SITE_LIST_CURRENCY_EMPTY_WARNING = '未从店铺站点列表解析到可用币种，售价可能无法提交';

function resolveCaps(shopCaps, shopType) {
  if (shopCaps && typeof shopCaps === 'object' && 'usesPriceInfo' in shopCaps) {
    return shopCaps;
  }
  return temuShopCapabilities(shopType);
}

export function usesSiteListCurrency(shopCapsOrType) {
  return Boolean(resolveCaps(shopCapsOrType, shopCapsOrType)?.usesPriceInfo);
}

/**
 * @returns {{
 *   source: 'site-list' | 'fill-in-standard',
 *   currencies: { code: string, name: string }[],
 *   code: string,
 *   defaultCode: string,
 *   emptyWarning: string,
 * }}
 */
export function resolveShopPublishCurrencies({
  shopType = '',
  shopCaps = null,
  siteList = null,
  fillInStandard = null,
  preferred = '',
  selectedSites = [],
  /** 半托/全托 PA：忽略历史 CNY，强制对齐店铺默认 USD */
  forceShopDefault = false,
} = {}) {
  const caps = resolveCaps(shopCaps, shopType);
  if (caps?.usesPriceInfo) {
    const currencies = extractSiteListCurrencies(siteList);
    if (currencies.length) {
      return {
        source: 'site-list',
        currencies,
        code: pickSiteListCurrency(siteList, { preferred, selectedSites }),
        defaultCode: currencies[0]?.code || '',
        emptyWarning: '',
      };
    }
    // Temu 站点列表未接入时，回退 goods.add 可用币种，避免误报 Shein 发布规范
  }
  const currencies = extractPublishCurrencies(fillInStandard);
  const defaultCode = extractDefaultPublishCurrency(fillInStandard) || currencies[0]?.code || '';
  // 半托/全托：若表单仍是旧默认 CNY，强制改用 USD，避免 goods.add 1000002
  const prefer = forceShopDefault ? '' : preferred;
  const pinnedDefault = forceShopDefault ? 'USD' : defaultCode;
  return {
    source: 'fill-in-standard',
    currencies,
    code: pickPublishCurrency(currencies, prefer, { defaultCode: pinnedDefault, forceDefault: forceShopDefault }),
    defaultCode,
    emptyWarning: currencies.length ? '' : FILL_IN_CURRENCY_EMPTY_WARNING,
  };
}

/**
 * 提交前校验表单币种是否落在当前店铺合法来源里。
 * Temu：publish/standard 返回 CNY/USD；规范未返回时不拦。
 * 自运营：优先站点列表，未接入则回退 publish/standard。
 */
export function validateShopPublishCurrency(form = {}, {
  shopType = '',
  shopCaps = null,
  siteList = null,
  fillInStandard = null,
  standardRaw = null,
} = {}) {
  const resolved = resolveShopPublishCurrencies({
    shopType: shopType || form?.shopType,
    shopCaps,
    siteList: siteList || form?.siteList,
    fillInStandard: fillInStandard || standardRaw,
    preferred: form?.currency,
    selectedSites: form?.selectedSites,
  });
  const cur = String(form?.currency || '').trim();
  if (resolved.source === 'site-list') {
    if (!resolved.currencies.length) {
      return {
        ok: false,
        message: resolved.emptyWarning || SITE_LIST_CURRENCY_EMPTY_WARNING,
        section: 'sales',
        field: 'currency',
        ...resolved,
      };
    }
    if (cur && !resolved.currencies.some((row) => String(row.code).toUpperCase() === cur.toUpperCase())) {
      return {
        ok: false,
        message: `售价币种「${cur}」不在店铺可售站点币种中（可用：${resolved.currencies.map((row) => row.code).join('、')}）`,
        section: 'sales',
        field: 'currency',
        ...resolved,
      };
    }
    return { ok: true, ...resolved };
  }
  if (resolved.currencies.length && cur
    && !resolved.currencies.some((row) => String(row.code).toUpperCase() === cur.toUpperCase())) {
    return {
      ok: false,
      message: `供货价币种「${cur}」不在 Temu 发品规范允许列表中（可用：${resolved.currencies.map((row) => row.code).join('、')}）`,
      section: 'sales',
      field: 'currency',
      ...resolved,
    };
  }
  return { ok: true, ...resolved };
}

/**
 * 自运营站点快照无币种时再拉一次平台站点列表。
 * @param {{ refreshSiteList?: () => Promise<{ siteList?: object } | object> }} opts
 */
export async function loadShopPublishCurrencies({
  shopType = '',
  shopCaps = null,
  siteList = null,
  fillInStandard = null,
  preferred = '',
  selectedSites = [],
  refreshSiteList,
} = {}) {
  let resolvedSiteList = siteList ?? null;
  const caps = resolveCaps(shopCaps, shopType);
  if (
    caps?.usesPriceInfo
    && !extractSiteListCurrencies(resolvedSiteList).length
    && typeof refreshSiteList === 'function'
  ) {
    try {
      const refreshed = await refreshSiteList();
      if (refreshed != null) {
        resolvedSiteList = refreshed.siteList ?? refreshed;
      }
    } catch {
      // 保留快照，由 emptyWarning 提示
    }
  }
  return {
    siteList: resolvedSiteList,
    ...resolveShopPublishCurrencies({
      shopCaps: caps,
      shopType,
      siteList: resolvedSiteList,
      fillInStandard,
      preferred,
      selectedSites,
    }),
  };
}
